import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { PLAY_STATES } from '../../stores/reducers/shots';
import { durationString } from '../../utils/utils';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import useShots from '../../contexts/shots/provider';
import { GreenroomState as GRS } from '../../stores/types/types';
import { useTotalShotDuration } from '../../hooks/use-shot-utilities';
import { useEffectiveShotDuration } from '../../hooks/use-effective-shot-duration';
import { BROADCAST_STATES } from '../../stores/types/broadcasting';

interface Props {
	isShowboardOpen: boolean;
	pinnedScrollPos?: number;
	playState: number;
	upcomingString: string;
	currentShotScrollPos: number;
	setScrollingPinned: Function;
}

export default function ShowboardHeader(props: Props) {
	const host = useSelector((state: GRS) => state.SessionReducer.host);
	const roomState = useSelector((state: GRS) => state.MeetingReducer.roomState);
	const shots = useSelector((state: GRS) => state.ShotsReducer.shots);
	const shotStartTimestampEntity = useSelector((state: GRS) => state.ShotsReducer.shotStartTimestampEntity?.entityValue?.start);
	const serverState = useSelector((state: GRS) => state.BroadcastReducer.serverState);
	const clearShot = useSelector((state: GRS) => state.SettingsReducer.clearShot);
	const {
		isShowboardOpen,
		pinnedScrollPos = 0,
		playState,
		upcomingString,
		currentShotScrollPos,
		setScrollingPinned,
	} = props;

	const { currentShot } = useShots();
	const totalDuration = useTotalShotDuration();
	const shotDuration = useEffectiveShotDuration(currentShot);

	const playingText = useMemo(() => {
		if (shotStartTimestampEntity && serverState === BROADCAST_STATES.BROADCAST_STARTED) {
			return 'LIVE';
		}
		if (shotStartTimestampEntity) {
			return 'Playing';
		}
		return 'Upcoming';
	}, [shotStartTimestampEntity, serverState]);

	const isFinalShot = useMemo(() => {
		return pinnedScrollPos === shots.length - 1;
	}, [pinnedScrollPos, shots]);

	return (
		<div className="showboard-header">
			{(roomState === ROOM_STATES.EDITING || !host) && (
				<>
					{isShowboardOpen && <span className="showboard-title">Showboard</span>}
					{isShowboardOpen && host && <span className="showboard-duration divider">• </span>}
					{host && <span className={classNames('showboard-duration', isShowboardOpen && 'open')}>{durationString(totalDuration, true)}</span>}
				</>
			)}
			{isShowboardOpen && (roomState === ROOM_STATES.SHOWTIME && host) && (
				<>
					{currentShotScrollPos === 0
						? (
							<div className="current-shot">
								<span>{playingText}</span>
							</div>
						) : (
							<button
								onClick={() => setScrollingPinned()}
								className="round padding-small padding-right-medium padding-left-medium background-transparent"
							>
								<Icon name={currentShotScrollPos > 0 ? ICONS.KEYBOARD_ARROW_RIGHT : ICONS.KEYBOARD_ARROW_LEFT} size={12} color={COLORS.LIGHT_GRAY} className="margin-right-small"/>
								<span>Back to current</span>
							</button>
						)
					}
					{((shotDuration || currentShot?.shot_data.autoDuration)
					&& shotStartTimestampEntity && !clearShot && upcomingString)
						? (
							<div className={classNames(isFinalShot ? 'final-shot' : 'next-shot', currentShotScrollPos === 0 && 'pinned')}>
								{!isFinalShot && <span>{currentShotScrollPos === 0 ? 'Next' : 'Next shot '}</span>}
								{(!isFinalShot || playState !== PLAY_STATES.LATE) ? (
									<span
										className={classNames(
											isFinalShot ? 'final-countdown' : 'next-countdown',
											{
												late: playState === PLAY_STATES.LATE && shotDuration,
												ending: playState === PLAY_STATES.ENDING || playState === PLAY_STATES.LATE && currentShot?.shot_data.autoDuration
											}
										)}
									>
										{
											isFinalShot
												? 'Ends in '
												: playState === PLAY_STATES.LATE && shotDuration
													? 'Late by '
													: 'Starts in '
										}
										{upcomingString}
									</span>
								) : (null)}
							</div>
						) : (
							null
						)
					}
				</>
			)}
		</div>
	);
}
